import React, { useEffect, useState } from 'react';
import { useSubscriptionPlan } from '../hooks/useSubscriptionPlan';
import { XmarkCircle } from 'iconoir-react';

interface AdBannerProps {
  position?: 'top' | 'bottom' | 'sidebar';
  className?: string;
}

export const AdBanner: React.FC<AdBannerProps> = ({ position = 'top', className = '' }) => {
  const { hasAds, loading, plan } = useSubscriptionPlan();
  const [dismissed, setDismissed] = useState(false);
  const [adContent, setAdContent] = useState<AdContent | null>(null);

  useEffect(() => {
    console.log('AdBanner - Debug:', { hasAds, loading, dismissed, plan, position });
    if (hasAds && !dismissed) {
      loadAd();
    }
  }, [hasAds, dismissed]);

  const loadAd = () => {
    // Simulation de chargement de publicité
    // Dans une version production, cela appellerait une régie publicitaire
    const mockAds: AdContent[] = [
      {
        id: 1,
        title: 'Communiquez via notre outil gratuit et touchez des milliers de personne chaque jour',
        description: 'Contactez notre régie',
        imageUrl: '/assets/ad-placeholder.jpg',
        link: 'mailto:ad@ventilio.fr',
        cta: 'En savoir plus',
      },
      {
        id: 2,
        title: 'Passez au Premium',
        description: 'Profitez de Ventilio sans publicité pour seulement 1,50€/mois',
        imageUrl: '/assets/ad-premium.jpg',
        link: '/register?plan=premium',
        cta: 'Passer au Premium',
      },
      {
        id: 3,
        title: 'Nouveautés Ventilio',
        description: 'Découvrez les dernières fonctionnalités de votre outil préféré',
        imageUrl: '/assets/ad-features.jpg',
        link: 'https://ventilio.fr/features',
        cta: 'Découvrir',
      },
    ];

    const randomAd = mockAds[Math.floor(Math.random() * mockAds.length)];
    setAdContent(randomAd);
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Stocker dans localStorage pour ne pas réafficher pendant cette session
    sessionStorage.setItem(`ad_dismissed_${position}`, 'true');
  };

  const handleAdClick = () => {
    // Tracking de clic (à implémenter avec votre solution d'analytics)
    console.log('Ad clicked:', adContent?.id);
  };

  const FORCE_SHOW_ADS = false;
  
  // Ne pas afficher si :
  // - Pas de publicités pour ce plan (sauf en mode debug)
  // - En cours de chargement
  // - Publicité fermée
  // - Pas de contenu publicitaire
  if ((!hasAds && !FORCE_SHOW_ADS) || loading || dismissed || !adContent) {
    return null;
  }

  // Vérifier si déjà fermée dans cette session
  if (sessionStorage.getItem(`ad_dismissed_${position}`) === 'true') {
    return null;
  }

  const positionClasses = {
    top: 'ad-banner-top',
    bottom: 'ad-banner-bottom',
    sidebar: 'ad-banner-sidebar',
  };

  return (
    <div className={`ad-banner ${positionClasses[position]} ${className}`}>
      <div className="ad-label">Publicité</div>
      <button 
        className="ad-close-btn" 
        onClick={handleDismiss}
        aria-label="Fermer la publicité"
      >
        <XmarkCircle width={20} height={20} />
      </button>

      <a 
        href={adContent.link} 
        className="ad-content"
        onClick={handleAdClick}
        target={adContent.link.startsWith('http') ? '_blank' : '_self'}
        rel={adContent.link.startsWith('http') ? 'noopener noreferrer' : undefined}
      >
        {adContent.imageUrl && (
          <div className="ad-image">
            <img src={adContent.imageUrl} alt={adContent.title} />
          </div>
        )}
        <div className="ad-text">
          <h4>{adContent.title}</h4>
          <p>{adContent.description}</p>
          {adContent.cta && (
            <span className="ad-cta">{adContent.cta}</span>
          )}
        </div>
      </a>
    </div>
  );
};

interface AdContent {
  id: number;
  title: string;
  description: string;
  imageUrl?: string;
  link: string;
  cta?: string;
}

// Composant pour afficher un placeholder pendant le développement
export const AdPlaceholder: React.FC<AdBannerProps> = ({ position = 'top', className = '' }) => {
  return (
    <div className={`ad-placeholder ${position} ${className}`}>
      <div className="ad-placeholder-content">
        <div className="ad-label">Publicité (Développement)</div>
        <p>Espace publicitaire - Plan gratuit uniquement</p>
        <small>Cette zone affichera des publicités pour les utilisateurs du plan gratuit</small>
      </div>
    </div>
  );
};
